import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import React from "react";
import { useToast } from "@/components/ui/use-toast";
import { ErrorType } from "@/types";
import { Event } from "@/types/event";
import warning from "@/assets/warning.svg";
import { useDeleteEventMutation } from "@/services/eventApi";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { FacilitySchedule } from "@/types/facility/schedule";

export function DataTableRowActions({
  data,
  refetch,
  handleEdit,
  access,
}: {
  data: FacilitySchedule;
  refetch: () => void;
  handleEdit: (data: FacilitySchedule) => void;
  access: string;
}) {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const [isdelete, setIsDelete] = React.useState(false);
  const [deleteEvent] = useDeleteEventMutation();
  const { toast } = useToast();

  const deleteRow = async () => {
    try {
      await deleteEvent({ event_id: data.general_info.id as number, org_id: orgId }).unwrap();

      toast({
        variant: "success",
        title: "Event Deleted Successfully",
      });

      // Refresh the table
      refetch();

    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Deletion",
          // replace the world schedule with event
          description: `${typedError.data?.detail?.replace("schedule", "event") || typedError.data?.message?.replace("schedule", "event")}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Deletion",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  return (
    <>
      <Dialog>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
            >
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-4" onClick={(e) => e.stopPropagation()}>
            <DialogTrigger asChild>
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                handleEdit(data)
              }}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
            </DialogTrigger>
            {
            data.general_info.status == "upcoming" &&
             <DropdownMenuItem onClick={(e) => {
              e.stopPropagation();
              setIsDelete(true)
            }}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
            }
          </DropdownMenuContent>
        </DropdownMenu>
      </Dialog>
      {isdelete && (
        <AlertDialog open={isdelete} onOpenChange={setIsDelete}>
          <AlertDialogContent onClick={(e) => e.stopPropagation()}>
            <AlertDialogHeader>
              <AlertDialogDescription>
                <div className="flex flex-col items-center  justify-center gap-4">
                  <img src={warning} alt="warning" className="w-18 h-18" />
                  <AlertDialogTitle className="text-xl font-semibold w-80 text-center">
                    Please confirm if you want to delete this event
                  </AlertDialogTitle>
                </div>
                <div className="w-full flex justify-between items-center gap-3 mt-4">
                  <AlertDialogCancel
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDelete(false)
                    }}
                    className="w-full border border-primary font-semibold"
                  >
                    <i className="fa fa-xmark text-base px-1 "></i>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteRow()
                    }}
                    className="w-full bg-primary !text-black font-semibold"
                  >
                    <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
                    Delete
                  </AlertDialogAction>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
